/// <reference types="vite/client" />
import { getEnv } from "@/lib/env";
import { isDevelopment } from "@/lib/utils";
import { ConfirmationDialogProvider } from "@/providers/confirmation-dialog-provider";
import { HeroUIProvider } from "@heroui/system";
import { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
} from "@tanstack/react-router";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Toaster } from "@/components/common/Toaster";
import appCss from "./globals.css?url";
import { TooltipProvider } from "@/components/common/Tooltip";

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  head: () => {
    const siteUrl = (getEnv.VITE_SITE_URL ?? "https://terminal.reactor.trade").replace(
      /\/$/,
      "",
    );
    const ogImageUrl = `${siteUrl}/images/og-card.png`;
    const title = "Reactor";
    const description = "Trade everything - memes, stocks, perps, defAI";

    return {
      meta: [
        { charSet: "utf-8" },
        {
          name: "viewport",
          content: "width=device-width, initial-scale=1",
        },
        { title },
        { name: "description", content: description },
        {
          name: "keywords",
          content: "Reactor, Trading, Defi, Dex, Memes, SOL",
        },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: title },
        { property: "og:url", content: siteUrl },
        { property: "og:image", content: ogImageUrl },
        { property: "og:locale", content: "en_US" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: ogImageUrl },
      ],
      links: [
        { rel: "stylesheet", href: appCss },
        { rel: "icon", href: "/favicon.ico", type: "image/x-icon" },
      ],
    };
  },
  scripts: () => [
    ...((!isDevelopment && [
      {
        src: "https://cloud.umami.is/script.js",
        "data-website-id": getEnv.VITE_UMAMI_TRACKING_ID,
        async: true,
        strategy: "afterInteractive",
      },
      {
        src: "https://www.googletagmanager.com/gtag/js?id=G-5QKXMCF7CB",
        async: true,
        strategy: "afterInteractive",
      },
      {
        children: `window.dataLayer = window.dataLayer || [];
                    function gtag(){dataLayer.push(arguments);}
                    gtag('js', new Date());

                    gtag('config', 'G-5QKXMCF7CB');`,
      },
    ]) ||
      []),
  ],

  component: RootLayout,
});

function RootLayout() {
  return (
    <html lang="en" className="fancy-scroll2 bg-background">
      <head>
        <HeadContent />
      </head>
      <body className="custom">
        {/* {isDevelopment && <ReactQueryDevtools initialIsOpen={false} />} */}
        <HeroUIProvider>
          <TooltipProvider>
            <ConfirmationDialogProvider>
              <Outlet />
              {!isDevelopment && <SpeedInsights />}
              <Toaster theme="dark" />
            </ConfirmationDialogProvider>
          </TooltipProvider>
        </HeroUIProvider>
        <Scripts />
      </body>
    </html>
  );
}
